import {
  BankOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  CrownOutlined,
  DeleteOutlined,
  EditOutlined,
  LockOutlined,
  MailOutlined,
  MessageOutlined,
  PhoneOutlined,
  PlusOutlined,
  SafetyCertificateOutlined,
  TeamOutlined,
  UserOutlined,
} from '@ant-design/icons';
import {
  Avatar,
  Badge,
  Button,
  Card,
  Col,
  Form,
  Input,
  Modal,
  message,
  Popconfirm,
  Row,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import type React from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import api from '../config/api';
import { baseLanguage } from '../i18n/locale';
import { useAuthStore } from '../store/authStore';
import { LOGIN_NOTICE_KEY } from './Login';

const { Title, Text } = Typography;
const { Option } = Select;

interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phone?: string;
  role: string;
  isActive: boolean;
  emailVerified: boolean;
  lastLogin?: string;
  createdAt: string;
  organizationId?: string;
  organization?: {
    id: string;
    name: string;
    slug: string;
  };
  companyId?: string;
  company?: {
    id: string;
    name: string;
    taxNumber?: string;
  };
  telegramId?: string;
  telegramUsername?: string;
  _count?: {
    events: number;
    tickets: number;
  };
  tickets?: {
    id: string;
    ticketNumber: string;
    event: {
      id: string;
      title: string;
      startDate: string;
    };
    status: string;
    companyId?: string;
  }[];
}

const Users: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [totalUsers, setTotalUsers] = useState(0);
  const [organizations, setOrganizations] = useState<any[]>([]);
  const [companies, setCompanies] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [form] = Form.useForm();
  const { user: currentUser, logout } = useAuthStore();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  useEffect(() => {
    fetchUsers();
    fetchOrganizations();
    fetchCompanies();
  }, []);

  const fetchUsers = async (page = 1, pageSize = 50) => {
    setLoading(true);
    try {
      const response = await api.get('/users', {
        params: {
          page,
          limit: pageSize,
        },
      });
      setUsers(response.data.data || []);
      setTotalUsers(response.data.total || 0);
    } catch (error) {
      message.error(t('users.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const fetchOrganizations = async () => {
    try {
      const response = await api.get('/organizations');
      setOrganizations(response.data || []);
    } catch (error) {
      console.error('Failed to fetch organizations:', error);
    }
  };

  const fetchCompanies = async () => {
    try {
      const response = await api.get('/companies');
      setCompanies(response.data || []);
    } catch (error) {
      console.error('Failed to fetch companies:', error);
    }
  };

  const handleCreate = () => {
    setEditingUser(null);
    form.resetFields();
    form.setFieldsValue({
      role: 'ATTENDEE',
      isActive: true,
    });
    setModalVisible(true);
  };

  const handleEdit = (record: User) => {
    setEditingUser(record);
    form.setFieldsValue({
      ...record,
      companyId: record.companyId,
      password: undefined, // Don't show existing password
    });
    setModalVisible(true);
  };

  const handleDelete = async (id: string) => {
    try {
      await api.delete(`/users/${id}`);
      message.success(t('users.deleted'));
      fetchUsers();
    } catch (error: any) {
      const msg = error?.response?.data?.message || error?.message || t('messages.delete_error');
      message.error(typeof msg === 'string' ? msg : t('messages.delete_error'));
    }
  };

  const handleSubmit = async (values: any) => {
    try {
      const data = {
        email: values.email,
        firstName: values.firstName,
        lastName: values.lastName,
        phone: values.phone,
        // A saját soron a szerepkör mező le van tiltva, de az antd az űrlap
        // tárolójából a letiltott mezőt is visszaadja — ne küldjük el, amit
        // nem engedünk átállítani. Ezen az ágon egy ORGANIZATION_ADMIN
        // egyébként a saját nevét sem tudná menteni: a szerver a *értéket*
        // nézi, nem a változást, és a 'Cannot assign admin roles' ágra fut.
        ...(editingSelf ? {} : { role: values.role }),
        isActive: values.isActive,
        organizationId: values.organizationId,
        companyId: values.companyId,
        ...(values.password && { password: values.password }),
        // Szerkesztéskor az új jelszó mellé a panel nyelve is megy: a munkatárs
        // ezen a nyelven kapja az értesítőt arról, hogy adminisztrátor állított
        // be jelszót. Létrehozáskor NEM: a létrehozó DTO nem ismeri a mezőt, és
        // a szerver elutasítaná (mérve: 400), új fiókról pedig nem megy levél.
        ...(editingUser && values.password && { lang: baseLanguage(i18n.language) }),
      };

      if (editingUser) {
        await api.patch(`/users/${editingUser.id}`, data);
        // Maszkolás közben a „saját" sor az IMITÁLT felhasználóé: az ő jelszava
        // változott, nem azé, aki a gép előtt ül — a maszkolás sora záródik, a
        // saját token él tovább, tehát nincs miért kilépni.
        if (editingSelf && values.password && !currentUser?.masquerade) {
          // A saját új jelszó a saját bejelentkezést is lezárta (#109): a
          // következő kérés 401 lenne. Rendezetten lépünk ki, és a bejelentkező
          // oldal mondja meg, miért. Az üzenet sessionStorage-ban megy: a router
          // state-jét a kijelentkezéskor lefutó ProtectedRoute-redirect
          // felülírná (mérve élőben).
          try {
            sessionStorage.setItem(LOGIN_NOTICE_KEY, 'password-changed');
          } catch {
            // privát mód / letiltott tárhely: az üzenet elmarad, a kilépés nem
          }
          setModalVisible(false);
          logout();
          navigate('/login', { replace: true });
          return;
        }
        message.success(t('users.updated'));
      } else {
        await api.post('/users', data);
        message.success(t('users.created'));
      }

      setModalVisible(false);
      fetchUsers();
    } catch (error: any) {
      message.error(error.response?.data?.message || t('users.operationFailed'));
    }
  };

  const handleToggleActive = async (userId: string, isActive: boolean) => {
    try {
      await api.patch(`/users/${userId}`, { isActive });
      message.success(isActive ? t('users.activated') : t('users.deactivated'));
      fetchUsers();
    } catch (error) {
      message.error(t('users.operationFailed'));
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'SUPER_ADMIN':
        return 'red';
      case 'ORGANIZATION_ADMIN':
        return 'orange';
      case 'EVENT_ADMIN':
        return 'blue';
      case 'SPEAKER':
        return 'purple';
      case 'ATTENDEE':
        return 'green';
      default:
        return 'default';
    }
  };

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'SUPER_ADMIN':
        return <CrownOutlined />;
      case 'ORGANIZATION_ADMIN':
        return <SafetyCertificateOutlined />;
      case 'EVENT_ADMIN':
        return <TeamOutlined />;
      case 'SPEAKER':
        return <MessageOutlined />;
      case 'ATTENDEE':
        return <UserOutlined />;
      default:
        return <UserOutlined />;
    }
  };

  const columns: ColumnsType<User> = [
    {
      title: t('users.columnUser'),
      key: 'user',
      render: (_, record) => (
        <Space>
          <Avatar icon={<UserOutlined />} style={{ backgroundColor: '#1890ff' }}>
            {record.firstName?.[0]}
            {record.lastName?.[0]}
          </Avatar>
          <Space direction="vertical" size={0}>
            <Text strong>
              {record.firstName} {record.lastName}
            </Text>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {record.email}
            </Text>
          </Space>
        </Space>
      ),
    },
    {
      title: t('users.columnRole'),
      dataIndex: 'role',
      key: 'role',
      filters: [
        { text: t('users.filterSuperAdmin'), value: 'SUPER_ADMIN' },
        { text: t('users.filterOrganizationAdmin'), value: 'ORGANIZATION_ADMIN' },
        { text: t('users.filterEventAdmin'), value: 'EVENT_ADMIN' },
        { text: t('users.filterSpeaker'), value: 'SPEAKER' },
        { text: t('users.filterAttendee'), value: 'ATTENDEE' },
      ],
      onFilter: (value, record) => record.role === value,
      render: (role) => (
        <Tag icon={getRoleIcon(role)} color={getRoleColor(role)}>
          {role}
        </Tag>
      ),
    },
    {
      title: t('users.columnCompany'),
      key: 'company',
      render: (_, record) =>
        record.company ? (
          <Tag color="purple" icon={<BankOutlined />}>
            {record.company.name}
          </Tag>
        ) : (
          <Text type="secondary">-</Text>
        ),
    },
    {
      title: t('users.columnStatus'),
      key: 'status',
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Switch
            checked={record.isActive}
            checkedChildren={t('users.active')}
            unCheckedChildren={t('users.inactive')}
            onChange={(checked) => handleToggleActive(record.id, checked)}
          />
          <Badge
            status={record.emailVerified ? 'success' : 'warning'}
            text={
              <Text style={{ fontSize: 12 }}>
                {record.emailVerified ? t('users.emailVerified') : t('users.emailNotVerified')}
              </Text>
            }
          />
        </Space>
      ),
    },
    {
      title: t('users.columnLastLogin'),
      dataIndex: 'lastLogin',
      key: 'lastLogin',
      render: (date) => (date ? dayjs(date).format('YYYY.MM.DD HH:mm') : '-'),
    },
    {
      title: t('users.columnRegistration'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date) => dayjs(date).format('YYYY.MM.DD'),
    },
    {
      title: t('common.actions'),
      key: 'action',
      render: (_, record) => (
        <Space size="small">
          <Tooltip title={t('users.edit')}>
            <Button type="link" icon={<EditOutlined />} onClick={() => handleEdit(record)} />
          </Tooltip>
          <Popconfirm
            title={t('common.confirm_delete')}
            onConfirm={() => handleDelete(record.id)}
            okText={t('common.yes')}
            cancelText={t('common.no')}
          >
            <Tooltip title={t('users.delete')}>
              <Button
                type="link"
                danger
                icon={<DeleteOutlined />}
                disabled={record.id === currentUser?.id}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // A szerver elutasítja a saját szerepkör átállítását (#80): admin fiókot a
  // felületen nem lehet létrehozni, az előléptetés SQL-ben történik, tehát aki
  // lefokozza magát, azt innen senki nem tudja visszaemelni. A mező letiltva
  // megy — az űrlap a változatlan értéket így is elküldi, amit a szerver
  // szándékosan átenged.
  const editingSelf = !!editingUser && editingUser.id === currentUser?.id;

  const roleOptions = [
    { value: 'ATTENDEE', labelKey: 'users.roleAttendee' },
    { value: 'SPEAKER', labelKey: 'users.roleSpeaker' },
    { value: 'EVENT_ADMIN', labelKey: 'users.roleEventAdmin' },
    { value: 'ORGANIZATION_ADMIN', labelKey: 'users.roleOrganizationAdmin' },
    { value: 'SUPER_ADMIN', labelKey: 'users.roleSuperAdmin' },
  ] as const;

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Title level={2}>{t('users.title')}</Title>
        </Col>
        <Col>
          <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
            {t('users.newUser')}
          </Button>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card>
            <Space>
              <Avatar icon={<UserOutlined />} style={{ backgroundColor: '#1890ff' }} size={48} />
              <div>
                <Text type="secondary">{t('users.statTotal')}</Text>
                <Title level={3} style={{ margin: 0 }}>
                  {totalUsers}
                </Title>
              </div>
            </Space>
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Space>
              <Avatar
                icon={<CheckCircleOutlined />}
                style={{ backgroundColor: '#52c41a' }}
                size={48}
              />
              <div>
                <Text type="secondary">{t('users.active')}</Text>
                <Title level={3} style={{ margin: 0 }}>
                  {users.filter((u) => u.isActive).length}
                </Title>
              </div>
            </Space>
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Space>
              <Avatar
                icon={<SafetyCertificateOutlined />}
                style={{ backgroundColor: '#faad14' }}
                size={48}
              />
              <div>
                <Text type="secondary">{t('users.statAdmins')}</Text>
                <Title level={3} style={{ margin: 0 }}>
                  {users.filter((u) => u.role === 'ADMIN' || u.role === 'SUPER_ADMIN').length}
                </Title>
              </div>
            </Space>
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Space>
              <Avatar icon={<MessageOutlined />} style={{ backgroundColor: '#722ed1' }} size={48} />
              <div>
                <Text type="secondary">{t('users.statTelegram')}</Text>
                <Title level={3} style={{ margin: 0 }}>
                  {users.filter((u) => u.telegramId).length}
                </Title>
              </div>
            </Space>
          </Card>
        </Col>
      </Row>

      <Card>
        <Table
          columns={columns}
          dataSource={users}
          rowKey="id"
          loading={loading}
          pagination={{
            total: totalUsers,
            defaultPageSize: 50,
            showSizeChanger: true,
            pageSizeOptions: ['20', '50', '100'],
            showTotal: (total) => t('users.paginationTotal', { count: total }),
            onChange: (page, pageSize) => {
              fetchUsers(page, pageSize);
            },
            onShowSizeChange: (current, size) => {
              fetchUsers(1, size);
            },
          }}
        />
      </Card>

      <Modal
        title={editingUser ? t('users.editTitle') : t('users.createTitle')}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={700}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="firstName"
                label={t('users.firstNameLabel')}
                rules={[{ required: true, message: t('common.requiredField') }]}
              >
                <Input prefix={<UserOutlined />} placeholder={t('users.firstNamePlaceholder')} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="lastName"
                label={t('users.lastNameLabel')}
                rules={[{ required: true, message: t('common.requiredField') }]}
              >
                <Input prefix={<UserOutlined />} placeholder={t('users.lastNamePlaceholder')} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="email"
                label={t('common.email')}
                rules={[
                  { required: true, message: t('common.requiredField') },
                  { type: 'email', message: t('users.emailInvalid') },
                ]}
              >
                <Input
                  prefix={<MailOutlined />}
                  placeholder={t('users.emailPlaceholder')}
                  disabled={!!editingUser}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="phone" label={t('users.phoneLabel')}>
                <Input prefix={<PhoneOutlined />} placeholder={t('users.phonePlaceholder')} />
              </Form.Item>
            </Col>
          </Row>

          {/* Szerkesztéskor is: üresen hagyva a jelszó nem változik. Az új
              jelszó a munkatárs minden bejelentkezését lezárja, és értesítőt
              kap; a saját soron a mentés kijelentkeztet (#109). */}
          <Form.Item
            name="password"
            label={editingUser ? t('users.newPasswordLabel') : t('users.passwordLabel')}
            extra={
              editingUser
                ? editingSelf && !currentUser?.masquerade
                  ? t('users.newPasswordHintSelf')
                  : t('users.newPasswordHint')
                : undefined
            }
            rules={[
              { required: !editingUser, message: t('common.requiredField') },
              { min: 6, message: t('users.passwordMin') },
            ]}
          >
            <Input.Password
              autoComplete="new-password"
              prefix={<LockOutlined />}
              placeholder={t('users.passwordMin')}
            />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="role"
                label={t('users.columnRole')}
                rules={[{ required: true, message: t('common.requiredField') }]}
                extra={editingSelf ? t('users.ownRoleLocked') : undefined}
              >
                <Select placeholder={t('users.selectRole')} disabled={editingSelf}>
                  {roleOptions.map((option) => (
                    <Option key={option.value} value={option.value}>
                      {t(option.labelKey)}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="organizationId" label={t('users.organizationLabel')}>
                <Select placeholder={t('users.selectOrganization')} allowClear>
                  {organizations.map((org) => (
                    <Option key={org.id} value={org.id}>
                      {org.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="companyId"
                label={t('users.companyLabel')}
                extra={t('users.companyExtra')}
              >
                <Select
                  placeholder={t('users.selectCompany')}
                  allowClear
                  showSearch
                  optionFilterProp="children"
                >
                  {companies.map((company) => (
                    <Option key={company.id} value={company.id}>
                      {company.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="isActive"
                label={t('users.columnStatus')}
                valuePropName="value"
                initialValue={true}
              >
                <Select>
                  <Option value={true}>{t('users.active')}</Option>
                  <Option value={false}>{t('users.inactive')}</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                {editingUser ? t('users.save') : t('common.create')}
              </Button>
              <Button onClick={() => setModalVisible(false)}>{t('users.cancel')}</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Users;
